import { Button } from '@monorepo/shared/componentsV2/Button';
import { ButtonLink } from '@monorepo/shared/componentsV2/Link';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import DoubleChevronLeftIcon from '@svg/chevron-left-double.svg';
import ChevronLeftIcon from '@svg/chevron-left.svg';
import DoubleChevronRightIcon from '@svg/chevron-right-double.svg';
import ChevronRightIcon from '@svg/chevron-right.svg';
import { addYears, endOfMonth, subDays, subMonths } from 'date-fns';
import { UTCEquivalentOfLocal } from 'mapistry-shared';
import React from 'react';
import { CalendarFilters } from '../CalendarFilters';
import { useComplianceCalendarContext } from './ComplianceCalendarContext';
import {
  CalendarViewType,
  getComplianceCalendarNow,
} from './helpers/complianceCalendarHelpers';
import { getListRangeDayCount } from './helpers/complianceCalendarListViewHelpers';
import {
  getWeekRangeContaining,
  shiftUtcDays,
  shiftUtcYears,
  WEEK_VIEW_DAY_COUNT,
} from './helpers/complianceCalendarWeekViewHelpers';
import { ListRangeDateButton } from './ListRangeDateButton';
import {
  CalendarHeaderArea,
  CalendarHeaderContainer,
  CalendarHeaderLayout,
  CalendarNavButton,
  CalendarTitle,
  ViewSelect,
} from './styled';

const NextButtonWithEndOfRangeText = ({
  isEnabled,
  onClick,
  children,
}: {
  isEnabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Tooltip
    title={
      !isEnabled
        ? 'You can view calendar events up to five years into the future.'
        : ''
    }
  >
    <CalendarNavButton
      variant="text"
      disabled={!isEnabled}
      onClick={onClick}
      focusableWhenDisabled
    >
      {children}
    </CalendarNavButton>
  </Tooltip>
);

/**
 *  For now, we only cache events up to 5 years into the future.
 *  If it's June 2025 now, the last month one can go to is May 2030.
 */
export function getCalendarRange() {
  const YEARS_INTO_THE_FUTURE = 5;
  const endOfLastMonth = endOfMonth(subMonths(new Date(), 1));
  const in5Years = addYears(endOfLastMonth, YEARS_INTO_THE_FUTURE);
  const utcEquivalent = UTCEquivalentOfLocal(in5Years);
  return { end: utcEquivalent };
}

const VIEW_OPTIONS = [
  { value: CalendarViewType.Month, label: 'Month' },
  { value: CalendarViewType.Week, label: 'Week' },
  { value: CalendarViewType.List, label: 'List' },
];

type ComplianceCalendarHeaderProps = {
  isTodaySectionInViewport: boolean;
  onGoToTodayInList: () => void;
};

export const ComplianceCalendarHeader = ({
  isTodaySectionInViewport,
  onGoToTodayInList,
}: ComplianceCalendarHeaderProps) => {
  const {
    goToNext,
    goToNextYear,
    goToPrevious,
    goToPreviousYear,
    goToToday,
    handleViewChange: onViewChange,
    title,
    updateVisibleRange,
    view,
    visibleRangeExclusiveEnd,
    visibleRangeStart,
  } = useComplianceCalendarContext();

  const handleViewChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const v = event.target.value as CalendarViewType;
    if (!Object.values(CalendarViewType).includes(v)) {
      throw new Error(
        'invalid calendar view type. must be one of: dayGridMonth, week, list',
      );
    }
    onViewChange(v);
  };

  const today = getComplianceCalendarNow();
  const todayIsVisible =
    visibleRangeStart < today && today < visibleRangeExclusiveEnd;

  const { end: endOfValidRange } = getCalendarRange();
  const isListView = view === CalendarViewType.List;

  const isTodayButtonDisabled = isListView
    ? isTodaySectionInViewport
    : todayIsVisible;
  const handleTodayClick = isListView ? onGoToTodayInList : goToToday;

  const nextWeekExclusiveEnd = shiftUtcDays(
    visibleRangeExclusiveEnd,
    WEEK_VIEW_DAY_COUNT,
  );
  const nextListExclusiveEnd = shiftUtcDays(
    visibleRangeExclusiveEnd,
    getListRangeDayCount(visibleRangeStart, visibleRangeExclusiveEnd),
  );

  // whether another week, list range, or month fits within the total limit (5 years)
  const hasRoomForNextWeek = endOfValidRange >= nextWeekExclusiveEnd;
  const hasRoomForNextList = endOfValidRange >= nextListExclusiveEnd;
  const hasRoomForNextMonth = endOfValidRange > visibleRangeExclusiveEnd;

  const canGoToNextMonthOrWeek = {
    [CalendarViewType.Week]: hasRoomForNextWeek,
    [CalendarViewType.List]: hasRoomForNextList,
    [CalendarViewType.Month]: hasRoomForNextMonth,
  }[view];

  const endOfLastMonthOneYearAhead = addYears(
    endOfMonth(subDays(visibleRangeExclusiveEnd, 1)),
    1,
  );
  const nextYearWeekExclusiveEnd = getWeekRangeContaining(
    shiftUtcYears(visibleRangeStart, 1),
  ).exclusiveEnd;
  const nextYearListExclusiveEnd = shiftUtcDays(
    shiftUtcYears(visibleRangeStart, 1),
    getListRangeDayCount(visibleRangeStart, visibleRangeExclusiveEnd),
  );

  const canGoToNextYear = {
    [CalendarViewType.Week]: endOfValidRange >= nextYearWeekExclusiveEnd,
    [CalendarViewType.List]: endOfValidRange >= nextYearListExclusiveEnd,
    [CalendarViewType.Month]: endOfValidRange >= endOfLastMonthOneYearAhead,
  }[view];

  const periodLabel = {
    [CalendarViewType.Week]: 'week',
    [CalendarViewType.List]: 'range',
    [CalendarViewType.Month]: 'month',
  }[view];

  const previousYearButton = (
    <CalendarNavButton variant="text" onClick={goToPreviousYear}>
      <VisuallyHidden>Previous year</VisuallyHidden>
      <DoubleChevronLeftIcon aria-hidden />
    </CalendarNavButton>
  );

  const previousButton = (
    <CalendarNavButton variant="text" onClick={goToPrevious}>
      <VisuallyHidden>Previous {periodLabel}</VisuallyHidden>
      <ChevronLeftIcon aria-hidden />
    </CalendarNavButton>
  );

  const nextButton = (
    <NextButtonWithEndOfRangeText
      isEnabled={canGoToNextMonthOrWeek}
      onClick={goToNext}
    >
      <VisuallyHidden>Next {periodLabel}</VisuallyHidden>
      <ChevronRightIcon aria-hidden />
    </NextButtonWithEndOfRangeText>
  );

  const nextYearButton = (
    <NextButtonWithEndOfRangeText
      isEnabled={canGoToNextYear}
      onClick={goToNextYear}
    >
      <VisuallyHidden>Next year</VisuallyHidden>
      <DoubleChevronRightIcon aria-hidden />
    </NextButtonWithEndOfRangeText>
  );

  return (
    <CalendarHeaderContainer>
      <CalendarHeaderLayout>
        <CalendarHeaderArea $area="left">
          <Button
            variant="outlined"
            color="primary"
            disabled={isTodayButtonDisabled}
            onClick={handleTodayClick}
          >
            Today
          </Button>
          <ViewSelect
            name="compliance-calendar-view"
            label="Calendar view"
            isLabelHidden
            dontSaveSpaceForErrorText
            options={VIEW_OPTIONS}
            value={view}
            onChange={handleViewChange}
            MenuProps={{
              // required to style the menu items
              disablePortal: true,
              // with disabling the portal, need to position it properly
              getContentAnchorEl: null,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: -8,
                horizontal: 'left',
              },
            }}
          />
        </CalendarHeaderArea>
        <CalendarHeaderArea $area="datePicker">
          {previousYearButton}
          {previousButton}

          {!isListView && <CalendarTitle>{title}</CalendarTitle>}
          {isListView && (
            <ListRangeDateButton
              exclusiveEnd={visibleRangeExclusiveEnd}
              maxDate={endOfValidRange}
              onRangeChange={updateVisibleRange}
              start={visibleRangeStart}
            />
          )}

          {nextButton}
          {nextYearButton}
        </CalendarHeaderArea>
        <CalendarHeaderArea $area="right">
          <CalendarFilters />
          <ButtonLink
            role="button"
            variant="contained"
            color="primary"
            to="?taskId=new"
          >
            Add Task
          </ButtonLink>
        </CalendarHeaderArea>
      </CalendarHeaderLayout>
    </CalendarHeaderContainer>
  );
};
